import { useLiquidGlassPress } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { hapticFeedback } from '@tma.js/sdk';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { GetJournalEntriesQuery, RitualType } from '../../client/generated/graphql';
import { getRitualConfig } from '../../helpers/ritual';
import { CommonIcon } from '../common/CommonIcon';

const ROW_DIVIDER_BORDER = '1px solid color-mix(in srgb, var(--tg-theme-text-color) 16%, transparent)';

type RatingJournalEntry = GetJournalEntriesQuery['journalEntries'][0] & { rating: number };

interface RatingEntrySectionProps {
    entry: RatingJournalEntry;
    timeLabel: string;
    showDivider?: boolean;
    onClick: () => void;
}

export const RatingEntrySection: FC<RatingEntrySectionProps> = ({ entry, timeLabel, showDivider = false, onClick }) => {
    const { t } = useTranslation();
    const ritualType = entry.ritualType as RitualType | null;
    const config = ritualType ? getRitualConfig(t)[ritualType] : null;
    const title = entry.taskName ?? t('rating.daily');

    const { pressed, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClick: onPressClick } = useLiquidGlassPress({
        preventDefault: true,
        onActivate: () => {
            hapticFeedback.impactOccurred.ifAvailable('light');
            onClick();
        },
    });

    return (
        <Box
            component="button"
            type="button"
            aria-label={title}
            onClick={onPressClick}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            sx={{
                display: 'block',
                width: '100%',
                m: 0,
                px: '16px',
                py: '14px',
                border: 'none',
                borderTop: showDivider ? ROW_DIVIDER_BORDER : 'none',
                background: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                font: 'inherit',
                color: 'inherit',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none',
                bgcolor: pressed ? 'color-mix(in srgb, var(--tg-theme-text-color) 6%, transparent)' : 'transparent',
                transition: 'background-color 0.12s ease',
            }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.25}>
                <Stack direction="row" alignItems="center" gap={1}>
                    {config && (
                        <CommonIcon
                            icon={config.icon}
                            type="outlined"
                            size={16}
                            color="var(--tg-theme-hint-color)"
                        />
                    )}
                    <Text style={{ fontSize: '14px', fontWeight: 600, color: 'var(--tg-theme-text-color)' }}>
                        {title}
                    </Text>
                </Stack>
                <Text style={{ fontSize: '12px', color: 'var(--tg-theme-hint-color)' }}>
                    {timeLabel}
                </Text>
            </Stack>

            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Text style={{ fontSize: '12px', color: 'var(--tg-theme-hint-color)' }}>
                    {t('rating.title')}
                </Text>
                <Box
                    sx={{
                        background: 'color-mix(in srgb, var(--tg-theme-button-color) 13%, transparent)',
                        borderRadius: '6px',
                        padding: '3px 10px',
                    }}
                >
                    <Text style={{ fontSize: '14px', fontWeight: 700, color: 'var(--tg-theme-button-color)' }}>
                        {entry.rating}/10
                    </Text>
                </Box>
            </Stack>
        </Box>
    );
};
